import React from 'react';
import {
    Box, Typography, Stack, Paper, Skeleton, Button, Pagination, Select, MenuItem,
    TextField, FormControl, InputLabel,
} from '@mui/material';
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined';
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined';

export const GRID_SX = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: 2,
    '@media (max-width:359px)': { gridTemplateColumns: '1fr' },
};

const focusSx = {
    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
};

export const LibraryShell = ({ title, description, actions, children }) => (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto', width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
            sx={{ mb: 3 }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {description}
                </Typography>
            </Box>
            {actions && <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>{actions}</Stack>}
        </Stack>
        {children}
    </Box>
);

export const ToolbarPaper = ({ children }) => (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 2, md: 3 }, mb: 3 }}>
        {children}
    </Paper>
);

export const FieldLabel = ({ children, htmlFor }) => (
    <Typography component="label" htmlFor={htmlFor} variant="body2" sx={{ display: 'block', fontWeight: 500, mb: 0.75 }}>
        {children}
    </Typography>
);

export const LinkButton = (props) => (
    <Button variant="outlined" sx={{ minHeight: 44, textTransform: 'none', ...focusSx }} {...props} />
);

export const VideoGridSkeleton = ({ count = 8 }) => (
    <Box sx={GRID_SX} aria-busy="true" aria-label="Loading videos">
        {Array.from({ length: count }).map((_, i) => (
            <Paper key={i} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Skeleton variant="rectangular" sx={{ width: '100%', aspectRatio: '16 / 9', height: 'auto' }} />
                <Box sx={{ p: 2 }}>
                    <Skeleton width="90%" />
                    <Skeleton width="60%" />
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                        <Skeleton variant="rounded" width={64} height={24} />
                        <Skeleton variant="rounded" width={64} height={24} />
                    </Stack>
                </Box>
            </Paper>
        ))}
    </Box>
);

export const LibraryEmpty = ({ message, hint, filtered, action }) => (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 3, md: 6 }, textAlign: 'center' }}>
        {filtered
            ? <SearchOffOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled' }} aria-hidden />
            : <VideoLibraryOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled' }} aria-hidden />}
        <Typography variant="h6" sx={{ mt: 1, fontWeight: 600 }}>{message}</Typography>
        {hint && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: action ? 2 : 0 }}>{hint}</Typography>}
        {action}
    </Paper>
);

export const ResultCount = ({ page, pageSize, total, loading }) => {
    if (loading || !total) return null;
    const from = (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, total);
    return (
        <Typography variant="body2" color="text.secondary" role="status">
            Showing {from}-{to} of {total} videos
        </Typography>
    );
};

export const PagerControls = ({ params, setParams, totalCount, videoLoading, hasVideos }) => {
    const maxPage = Math.max(1, Math.ceil(totalCount / params.page_size));
    return (
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 140 }} disabled={videoLoading || !hasVideos}>
                <InputLabel id="page-size-label">Per page</InputLabel>
                <Select
                    labelId="page-size-label"
                    label="Per page"
                    value={params.page_size}
                    sx={{ minHeight: 44 }}
                    onChange={(e) => {
                        let value = parseInt(e.target.value);
                        if (value < 12) value = 12;
                        if (value > 48) value = 48;
                        setParams(prev => ({ ...prev, page_size: value, page: 1 }));
                    }}
                >
                    {[12, 24, 48].map(size => (
                        <MenuItem key={size} value={size}>{size} per page</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <TextField
                sx={{ width: 90 }}
                label="Page" type="number" size="small"
                value={params.page}
                inputProps={{ min: 1, max: maxPage, style: { minHeight: 28 } }}
                onChange={e => {
                    let value = parseInt(e.target.value);
                    if (value < 1) value = 1;
                    if (value > maxPage) value = maxPage;
                    setParams(prev => ({ ...prev, page: value }));
                }}
            />
        </Stack>
    );
};

export const PaginationFoot = ({ page, totalCount, pageSize, setParams, loading }) => (
    <Stack spacing={1} alignItems="center" sx={{ mt: 4 }}>
        <ResultCount page={page} pageSize={pageSize} total={totalCount} loading={loading} />
        <Pagination
            disabled={loading}
            count={Math.ceil(totalCount / pageSize)}
            page={page}
            onChange={(event, value) => setParams(prev => ({ ...prev, page: value }))}
            color="primary"
            siblingCount={0}
            boundaryCount={1}
            sx={{ '& .MuiPaginationItem-root': { minWidth: 44, height: 44, borderRadius: 2 } }}
        />
    </Stack>
);
